import React, { useState, useEffect } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import {
  ArrowUpRight, ArrowDownRight, Activity, Zap, Award, DollarSign, Users, Briefcase,
  Phone, Mail, FileText
} from 'lucide-react';
import { dashboardAPI, tasksAPI, usersAPI, timeLogsAPI, projectsAPI } from '../services/api';
import { motion } from 'framer-motion';

// Helper to check if two dates are on the same day
const isSameDay = (d1Str, d2) => {
  if (!d1Str || !d2) return false;
  const d1 = new Date(d1Str);
  return d1.getFullYear() === d2.getFullYear() &&
         d1.getMonth() === d2.getMonth() &&
         d1.getDate() === d2.getDate();
};

// Helper to get Monday of the current week
const getMonday = (d) => {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(date.setDate(diff));
  monday.setHours(0,0,0,0);
  return monday;
};

// Relative time formatter for the activity feed (e.g. "2h ago", "3d ago")
const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const then = new Date(dateStr).getTime();
  const diffMs = Date.now() - then;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
};

const ACTIVITY_ICONS = { call: Phone, email: Mail, meeting: Users, note: FileText };

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        padding: '12px 16px',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
      }}>
        <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: 'var(--text-muted)' }}>{label}</p>
        <p style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)' }}>
          {payload[0].value} <span style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-muted)' }}>hrs</span>
        </p>
      </div>
    );
  }
  return null;
};

const containerVariants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 24 } }
};

// Reusable horizontal bar list used for status / priority / pipeline breakdowns
const StatBarList = ({ items, emptyLabel, textPrimary, textSecondary, trackBg }) => {
  const total = items.reduce((sum, it) => sum + it.count, 0);
  if (total === 0) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: textSecondary }}>
        {emptyLabel}
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      {items.filter(it => it.count > 0).map((it) => {
        const pct = Math.round((it.count / total) * 100);
        return (
          <div key={it.label} style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
              <span style={{ fontWeight: 600, color: textPrimary, display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: it.color, display: 'inline-block' }} />
                {it.label}
              </span>
              <span style={{ color: textSecondary, fontSize: '11px' }}>{it.count} ({pct}%)</span>
            </div>
            <div style={{ width: '100%', height: '8px', background: trackBg, borderRadius: '4px', overflow: 'hidden' }}>
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${pct}%` }}
                transition={{ duration: 0.8, delay: 0.1, type: 'spring' }}
                style={{ height: '100%', background: it.color, borderRadius: '4px' }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default function Reports() {
  const [loading, setLoading] = useState(true);

  // Person filter: 'all' = overall report, otherwise a specific user_id
  const [selectedUserId, setSelectedUserId] = useState('all');
  // Holds the raw fetched lists so the filter can recompute without re-fetching
  const [rawData, setRawData] = useState(null);
  const [filteredHours, setFilteredHours] = useState(0);

  // Data State
  const [summary, setSummary] = useState(null);
  const [timeData, setTimeData] = useState([]);
  const [workload, setWorkload] = useState([]);
  const [topPerformer, setTopPerformer] = useState(null);
  const [progressData, setProgressData] = useState({ progress: 0, completion: 0 });
  const [taskStatusData, setTaskStatusData] = useState([]);
  const [taskPriorityData, setTaskPriorityData] = useState([]);
  const [projectStatusData, setProjectStatusData] = useState([]);
  const [dealsPipeline, setDealsPipeline] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [extraKpis, setExtraKpis] = useState({ revenue: 0, dealsWon: 0, dealsTotal: 0, leads: 0, clients: 0 });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [dashRes, tasksRes, usersRes, logsRes, taskStatusRes, dealsPipelineRes, activitiesRes, projectsRes] = await Promise.all([
          dashboardAPI.summary(),
          tasksAPI.list(),
          usersAPI.list(),
          timeLogsAPI.list(),
          dashboardAPI.tasksByStatus().catch(() => ({ data: { data: [] } })),
          dashboardAPI.dealsPipeline().catch(() => ({ data: { data: [] } })),
          dashboardAPI.recentActivities().catch(() => ({ data: { data: [] } })),
          projectsAPI.list().catch(() => ({ data: { data: [] } })),
        ]);

        const summaryData = dashRes.data?.data || {};
        const allTasks = tasksRes.data?.data || [];
        const allUsers = usersRes.data?.data || [];
        const allLogs = logsRes.data?.data || [];
        const taskStatusRaw = taskStatusRes.data?.data || [];
        const dealsPipelineRaw = dealsPipelineRes.data?.data || [];
        const activitiesRaw = activitiesRes.data?.data || [];
        const allProjects = projectsRes.data?.data || [];

        setSummary(summaryData);
        setExtraKpis({
          revenue: summaryData.revenue || 0,
          dealsWon: summaryData.deals?.won || 0,
          dealsTotal: summaryData.deals?.total || 0,
          leads: summaryData.leads || 0,
          clients: summaryData.clients || 0,
        });

        // Progress & Completion Metrics
        const totalProjects = summaryData.projects?.total || 0;
        const activeProjects = summaryData.projects?.active || 0;
        const completedProjects = totalProjects - activeProjects;
        const progress = totalProjects > 0 ? Math.round((completedProjects / totalProjects) * 100) : 0;
        
        const totalTasks = summaryData.tasks?.total || 0;
        const openTasks = summaryData.tasks?.open || 0;
        const completedTasks = totalTasks - openTasks;
        const completion = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
        
        setProgressData({ progress, completion });

        // Task Status Breakdown (live from backend group-by)
        // NOTE: Task Status Breakdown, Task Priority Breakdown, Time Tracked,
        // Workload and Top Performer are now computed by the person-filter
        // effect below (they depend on `selectedUserId`), using the raw
        // lists stashed into `rawData` at the end of this fetch.

        // Project Status Breakdown (computed from live project list)
        const projectStatusColors = {
          'Backlog': '#64748b', 'To Do': '#3b82f6', 'In Progress': '#8b84ff',
          'In Review': '#f59e0b', 'Done': '#10b981'
        };
        const projectStatusCounts = {};
        allProjects.forEach(p => { projectStatusCounts[p.status] = (projectStatusCounts[p.status] || 0) + 1; });
        setProjectStatusData(
          Object.keys(projectStatusCounts).map(status => ({
            label: status,
            count: projectStatusCounts[status],
            color: projectStatusColors[status] || '#8b84ff'
          }))
        );

        // Deals Pipeline (live from backend group-by)
        const stageColors = { Prospect: '#64748b', Proposal: '#3b82f6', Negotiation: '#f59e0b', Won: '#10b981', Lost: '#ef4444' };
        const stageOrder = ['Prospect', 'Proposal', 'Negotiation', 'Won', 'Lost'];
        const pipelineByStage = {};
        dealsPipelineRaw.forEach(row => { pipelineByStage[row.stage] = row; });
        setDealsPipeline(
          stageOrder.map(stage => ({
            label: stage,
            count: Number(pipelineByStage[stage]?.count) || 0,
            value: Number(pipelineByStage[stage]?.total_value) || 0,
            color: stageColors[stage]
          }))
        );

        // Stash raw lists for the person-filter effect below to consume.
        setRawData({ allTasks, allLogs, allUsers, activitiesRaw, taskStatusRaw });

      } catch (err) {
        console.error('Failed to fetch report data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Recomputes all person-scoped sections whenever the selected person
  // (or the underlying raw data) changes. 'all' reproduces the original
  // overall report; any other value scopes everything to that one person.
  useEffect(() => {
    if (!rawData) return;
    const { allTasks, allLogs, allUsers, activitiesRaw, taskStatusRaw } = rawData;

    const tasksForUser = selectedUserId === 'all'
      ? allTasks
      : allTasks.filter(t => t.assigned_to === selectedUserId);
    const logsForUser = selectedUserId === 'all'
      ? allLogs
      : allLogs.filter(l => l.user_id === selectedUserId);
    const activitiesForUser = selectedUserId === 'all'
      ? activitiesRaw
      : activitiesRaw.filter(a => (a.user_id || a.user?.id || a.user?.user_id) === selectedUserId);

    // Task Completion % (scoped to the selected person's tasks)
    const totalTasks = tasksForUser.length;
    const openTasks = tasksForUser.filter(t => (t.status || '').toLowerCase() !== 'done').length;
    const completedTasks = totalTasks - openTasks;
    const completion = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
    setProgressData(prev => ({ ...prev, completion }));

    // Task Status Breakdown
    const statusColors = {
      'Backlog': '#64748b', 'To Do': '#3b82f6', 'In Progress': '#8b84ff',
      'In Review': '#f59e0b', 'Pending Review': '#f59e0b', 'Rework': '#ef4444', 'Done': '#10b981'
    };
    if (selectedUserId === 'all') {
      setTaskStatusData(
        taskStatusRaw.map(row => ({
          label: row.status,
          count: Number(row.count) || 0,
          color: statusColors[row.status] || '#8b84ff'
        }))
      );
    } else {
      const statusCounts = {};
      tasksForUser.forEach(t => { statusCounts[t.status] = (statusCounts[t.status] || 0) + 1; });
      setTaskStatusData(
        Object.keys(statusCounts).map(status => ({
          label: status,
          count: statusCounts[status],
          color: statusColors[status] || '#8b84ff'
        }))
      );
    }

    // Task Priority Breakdown
    const priorityColors = { Low: '#64748b', Medium: '#3b82f6', High: '#f59e0b', Critical: '#ef4444' };
    const priorityOrder = ['Critical', 'High', 'Medium', 'Low'];
    setTaskPriorityData(
      priorityOrder.map(p => ({
        label: p,
        count: tasksForUser.filter(t => t.priority === p).length,
        color: priorityColors[p]
      }))
    );

    // Recent Activity
    setRecentActivities(activitiesForUser.slice(0, 8));

    // Time Tracked Chart
    const monday = getMonday(new Date());
    const daysShort = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const weekDays = Array.from({ length: 7 }).map((_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      return d;
    });
    const chartData = weekDays.map((date, i) => {
      const dayLogs = logsForUser.filter(log => isSameDay(log.date || log.created_at, date));
      const hours = dayLogs.reduce((sum, log) => sum + Number(log.hours_spent || 0), 0);
      return { day: daysShort[i], hours: Number(hours.toFixed(1)) };
    });
    setTimeData(chartData);

    // Total Hours Logged KPI (scoped)
    const totalHoursForUser = logsForUser.reduce((sum, log) => sum + Number(log.hours_spent || 0), 0);
    setFilteredHours(Number(totalHoursForUser.toFixed(1)));

    // Workload & Top Performer / Person Performance
    const usersToShow = selectedUserId === 'all'
      ? allUsers
      : allUsers.filter(u => u.user_id === selectedUserId);

    if (usersToShow.length > 0) {
      const userStats = usersToShow.map(user => {
        const userTasks = allTasks.filter(t => t.assigned_to === user.user_id);
        const userLogs = allLogs.filter(l => l.user_id === user.user_id);
        const hoursLogged = userLogs.reduce((sum, log) => sum + Number(log.hours_spent || 0), 0);
        const tasksClosed = userTasks.filter(t => (t.status || '').toLowerCase() === 'done').length;

        const activeTasks = userTasks.length - tasksClosed;
        let status = 'Balanced';
        if (activeTasks > 8) status = 'High load';
        else if (activeTasks < 3) status = 'Under-used';

        return { ...user, activeTasks, tasksClosed, hoursLogged: Number(hoursLogged.toFixed(1)), status };
      }).filter(u => selectedUserId !== 'all' || u.activeTasks > 0 || u.tasksClosed > 0);

      if (selectedUserId === 'all') {
        const sortedByPerformance = [...userStats].sort((a, b) => b.tasksClosed - a.tasksClosed);
        setTopPerformer(sortedByPerformance[0] || null);
        setWorkload(userStats.sort((a, b) => b.activeTasks - a.activeTasks));
      } else {
        setTopPerformer(userStats[0] || null);
        setWorkload(userStats);
      }
    } else {
      setTopPerformer(null);
      setWorkload([]);
    }
  }, [selectedUserId, rawData]);

  const getInitials = (name) => {
    if (!name) return '??';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  // Dark theme using CSS variables to match the rest of the app
  const theme = {
    bg: 'var(--bg)',
    cardBg: 'var(--bg-card)',
    textPrimary: 'var(--text-primary)',
    textSecondary: 'var(--text-muted)',
    accentMain: 'var(--accent)',
    accentLight: 'var(--accent-surface, rgba(139,132,255,0.15))',
    border: 'var(--border)',
    borderSoft: 'var(--border-bright)',
    green: 'var(--color-green, #10b981)',
    red: 'var(--color-pink, #ef4444)'
  };

  const cardStyle = {
    backgroundColor: 'var(--bg-card)',
    borderRadius: '12px',
    padding: '20px',
    border: '1px solid var(--border)',
    boxShadow: 'none',
    display: 'flex',
    flexDirection: 'column'
  };

  if (loading) {
    return (
      <div style={{ background: 'var(--bg)', minHeight: '100vh', padding: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: 'var(--text-primary)', fontSize: '18px', fontWeight: 600 }}>Loading Reports...</div>
      </div>
    );
  }

  return (
    <div style={{ 
      background: 'var(--bg)', 
      minHeight: '100vh', 
      padding: '32px',
      fontFamily: 'Manrope, var(--font-body), sans-serif'
    }}>
      
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <p style={{ margin: '0 0 6px', color: 'var(--accent)', font: '600 11px/1.2 "JetBrains Mono", monospace', letterSpacing: '.14em', textTransform: 'uppercase' }}>Workspace / Reports</p>
          <h1 style={{ color: 'var(--text-primary)', fontSize: '24px', fontWeight: 700, margin: 0 }}>
            {selectedUserId === 'all'
              ? 'Reports Overview'
              : `Report · ${rawData?.allUsers?.find(u => u.user_id === selectedUserId)?.name || 'Team Member'}`}
          </h1>
        </div>

        {/* Person filter: 'All' shows the overall report, picking a person scopes everything to them */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Users size={16} color="var(--text-muted)" />
          <select
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value === 'all' ? 'all' : parseInt(e.target.value, 10))}
            style={{
              background: 'var(--bg-card)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              padding: '10px 14px',
              fontSize: '13px',
              fontWeight: 600,
              outline: 'none',
              cursor: 'pointer',
              minWidth: '220px'
            }}
          >
            <option value="all">All Team Members (Overall)</option>
            {(rawData?.allUsers || []).map(u => (
              <option key={u.user_id} value={u.user_id}>{u.name}</option>
            ))}
          </select>
        </div>
      </div>

      <motion.div 
        variants={containerVariants}
        initial="hidden"
        animate="show"
        style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(12, 1fr)', 
          gap: '20px', 
          marginBottom: '20px' 
        }}
      >
        
        {/* KPI 1: Project Progress */}
        <motion.div variants={itemVariants} style={{ gridColumn: 'span 3' }}>
          <div style={cardStyle}>
            <div style={{ fontSize: '13px', color: theme.textSecondary, fontWeight: 600, marginBottom: '8px' }}>
              Project Progress
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '16px' }}>
              <div style={{ fontSize: '32px', fontWeight: 700, color: theme.textPrimary, lineHeight: 1 }}>
                {progressData.progress}%
              </div>
              <div style={{ width: '60px', height: '30px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={[{v: 10}, {v: progressData.progress}, {v: 100}]}>
                    <Area type="monotone" dataKey="v" stroke="var(--accent)" fill="var(--accent-surface, rgba(139,132,255,0.15))" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', borderTop: '1px solid var(--border)', paddingTop: '12px' }}>
              <span style={{ color: 'var(--text-muted)' }}>Target</span>
              <span style={{ color: 'var(--color-green, #10b981)', fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                <ArrowUpRight size={14} /> 100%
              </span>
            </div>
          </div>
        </motion.div>

        {/* KPI 2: Task Completion */}
        <motion.div variants={itemVariants} style={{ gridColumn: 'span 3' }}>
          <div style={cardStyle}>
            <div style={{ fontSize: '13px', color: theme.textSecondary, fontWeight: 600, marginBottom: '8px' }}>
              Task Completion
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '16px' }}>
              <div style={{ fontSize: '32px', fontWeight: 700, color: theme.textPrimary, lineHeight: 1 }}>
                {progressData.completion}%
              </div>
              <div style={{ width: '60px', height: '30px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={[{v: 20}, {v: progressData.completion}, {v: 80}]}>
                    <Area type="monotone" dataKey="v" stroke="var(--accent)" fill="var(--accent-surface, rgba(139,132,255,0.15))" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', borderTop: '1px solid var(--border)', paddingTop: '12px' }}>
              <span style={{ color: 'var(--text-muted)' }}>Prev: {Math.max(0, progressData.completion - 5)}%</span>
              <span style={{ color: 'var(--color-green, #10b981)', fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                <ArrowUpRight size={14} /> +5.0%
              </span>
            </div>
          </div>
        </motion.div>

        {/* KPI 3: Productivity Score */}
        <motion.div variants={itemVariants} style={{ gridColumn: 'span 3' }}>
          <div style={cardStyle}>
            <div style={{ fontSize: '13px', color: theme.textSecondary, fontWeight: 600, marginBottom: '8px' }}>
              Productivity Score
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '16px' }}>
              <div style={{ fontSize: '32px', fontWeight: 700, color: theme.textPrimary, lineHeight: 1 }}>
                {(progressData.completion / 10).toFixed(1)}
              </div>
              <Activity size={28} color="var(--accent)" opacity={0.5} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', borderTop: '1px solid var(--border)', paddingTop: '12px' }}>
              <span style={{ color: 'var(--text-muted)' }}>Max: 10.0</span>
              <span style={{ color: 'var(--color-green, #10b981)', fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                <ArrowUpRight size={14} /> Peak
              </span>
            </div>
          </div>
        </motion.div>

        {/* KPI 4: Total Hours */}
        <motion.div variants={itemVariants} style={{ gridColumn: 'span 3' }}>
          <div style={cardStyle}>
            <div style={{ fontSize: '13px', color: theme.textSecondary, fontWeight: 600, marginBottom: '8px' }}>
              Total Hours Logged
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '16px' }}>
              <div style={{ fontSize: '32px', fontWeight: 700, color: theme.textPrimary, lineHeight: 1 }}>
                {selectedUserId === 'all' ? (summary?.hours_logged || 0) : filteredHours}
              </div>
              <Zap size={28} color="var(--accent)" opacity={0.5} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', borderTop: '1px solid var(--border)', paddingTop: '12px' }}>
              <span style={{ color: 'var(--text-muted)' }}>{selectedUserId === 'all' ? 'All Projects' : 'This Person'}</span>
              <span style={{ color: 'var(--color-pink, #ef4444)', fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                <ArrowDownRight size={14} /> -1.2%
              </span>
            </div>
          </div>
        </motion.div>

        {selectedUserId === 'all' && (
          <>
            {/* KPI 5: Revenue */}
            <motion.div variants={itemVariants} style={{ gridColumn: 'span 3' }}>
          <div style={cardStyle}>
            <div style={{ fontSize: '13px', color: theme.textSecondary, fontWeight: 600, marginBottom: '8px' }}>
              Revenue Won
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '16px' }}>
              <div style={{ fontSize: '32px', fontWeight: 700, color: theme.textPrimary, lineHeight: 1 }}>
                ${Number(extraKpis.revenue).toLocaleString()}
              </div>
              <DollarSign size={28} color="var(--accent)" opacity={0.5} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', borderTop: '1px solid var(--border)', paddingTop: '12px' }}>
              <span style={{ color: 'var(--text-muted)' }}>From won deals</span>
              <span style={{ color: theme.green, fontWeight: 600 }}>{extraKpis.dealsWon} won</span>
            </div>
          </div>
        </motion.div>

        {/* KPI 6: Deals Pipeline */}
        <motion.div variants={itemVariants} style={{ gridColumn: 'span 3' }}>
          <div style={cardStyle}>
            <div style={{ fontSize: '13px', color: theme.textSecondary, fontWeight: 600, marginBottom: '8px' }}>
              Deals in Pipeline
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '16px' }}>
              <div style={{ fontSize: '32px', fontWeight: 700, color: theme.textPrimary, lineHeight: 1 }}>
                {extraKpis.dealsTotal}
              </div>
              <Briefcase size={28} color="var(--accent)" opacity={0.5} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', borderTop: '1px solid var(--border)', paddingTop: '12px' }}>
              <span style={{ color: 'var(--text-muted)' }}>Win rate</span>
              <span style={{ color: theme.green, fontWeight: 600 }}>
                {extraKpis.dealsTotal > 0 ? Math.round((extraKpis.dealsWon / extraKpis.dealsTotal) * 100) : 0}%
              </span>
            </div>
          </div>
        </motion.div>

        {/* KPI 7: Leads */}
        <motion.div variants={itemVariants} style={{ gridColumn: 'span 3' }}>
          <div style={cardStyle}>
            <div style={{ fontSize: '13px', color: theme.textSecondary, fontWeight: 600, marginBottom: '8px' }}>
              Total Leads
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '16px' }}>
              <div style={{ fontSize: '32px', fontWeight: 700, color: theme.textPrimary, lineHeight: 1 }}>
                {extraKpis.leads}
              </div>
              <Users size={28} color="var(--accent)" opacity={0.5} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', borderTop: '1px solid var(--border)', paddingTop: '12px' }}>
              <span style={{ color: 'var(--text-muted)' }}>In the funnel</span>
            </div>
          </div>
        </motion.div>

        {/* KPI 8: Clients */}
        <motion.div variants={itemVariants} style={{ gridColumn: 'span 3' }}>
          <div style={cardStyle}>
            <div style={{ fontSize: '13px', color: theme.textSecondary, fontWeight: 600, marginBottom: '8px' }}>
              Active Clients
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '16px' }}>
              <div style={{ fontSize: '32px', fontWeight: 700, color: theme.textPrimary, lineHeight: 1 }}>
                {extraKpis.clients}
              </div>
              <Award size={28} color="var(--accent)" opacity={0.5} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', borderTop: '1px solid var(--border)', paddingTop: '12px' }}>
              <span style={{ color: 'var(--text-muted)' }}>Total on record</span>
            </div>
          </div>
        </motion.div>
          </>
        )}

        {/* Middle Row */}
        
        {/* Time Tracked (Matching the Bar Chart card aesthetic from image, using Area for visual interest) */}
        <motion.div variants={itemVariants} style={{ gridColumn: 'span 4' }}>
          <div style={{ ...cardStyle, height: '320px' }}>
            <div style={{ fontSize: '14px', color: theme.textPrimary, fontWeight: 700, marginBottom: '4px' }}>
              Time Tracked by Day
            </div>
            <div style={{ fontSize: '12px', color: theme.textSecondary, marginBottom: '24px' }}>
              Drilldown: Dates {'>'} Hours
            </div>
            <div style={{ flex: 1, position: 'relative' }}>
              {timeData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={timeData} margin={{ top: 10, right: 0, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="accentGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8b84ff" stopOpacity={0.5}/>
                        <stop offset="95%" stopColor="#8b84ff" stopOpacity={0.05}/>
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fill: 'var(--text-muted)', fontSize: 12 }} dy={10} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: 'var(--text-muted)', fontSize: 12 }} />
                    <Tooltip content={<CustomTooltip />} cursor={{ stroke: 'var(--border-bright)', strokeWidth: 1, strokeDasharray: '4 4' }} />
                    <Area type="monotone" dataKey="hours" stroke="#8b84ff" strokeWidth={3} fillOpacity={1} fill="url(#accentGradient)" activeDot={{ r: 6, fill: '#8b84ff', stroke: 'var(--bg-card)', strokeWidth: 2 }} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.textSecondary }}>
                  No time logged this week.
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Top Performer (Matching the Pie Chart card aesthetic) */}
        <motion.div variants={itemVariants} style={{ gridColumn: 'span 4' }}>
          <div style={{ ...cardStyle, height: '320px' }}>
            <div style={{ fontSize: '14px', color: theme.textPrimary, fontWeight: 700, marginBottom: '4px' }}>
              {selectedUserId === 'all' ? 'Top Performer' : 'Person Performance'}
            </div>
            <div style={{ fontSize: '12px', color: theme.textSecondary, marginBottom: '24px' }}>
              {selectedUserId === 'all' ? 'Based on Tasks Closed' : 'Selected team member'}
            </div>
            
            {topPerformer ? (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ position: 'relative', marginBottom: '16px' }}>
                  {/* Faux Pie chart ring around avatar */}
                  <svg width="100" height="100" style={{ position: 'absolute', top: -10, left: -10 }}>
                    <circle cx="50" cy="50" r="45" fill="none" stroke="var(--border-bright)" strokeWidth="4" />
                    <circle cx="50" cy="50" r="45" fill="none" stroke="#8b84ff" strokeWidth="4" strokeDasharray="200" strokeDashoffset="50" strokeLinecap="round" />
                  </svg>
                  <div style={{ 
                    width: '80px', height: '80px', borderRadius: '50%', 
                    background: 'var(--bg-hover)', 
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'var(--accent)', fontSize: '24px', fontWeight: '800',
                  }}>
                    {getInitials(topPerformer.name)}
                  </div>
                </div>
                
                <div style={{ fontSize: '18px', fontWeight: '700', color: theme.textPrimary }}>{topPerformer.name}</div>
                <div style={{ color: theme.textSecondary, fontSize: '13px', fontWeight: 500, marginBottom: '20px' }}>{topPerformer.role || 'Member'}</div>
                
                <div style={{ display: 'flex', gap: '32px', width: '100%', justifyContent: 'center' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '18px', fontWeight: 700, color: theme.textPrimary }}>{topPerformer.tasksClosed}</div>
                    <div style={{ fontSize: '11px', color: theme.textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Tasks</div>
                  </div>
                  <div style={{ width: '1px', background: 'var(--border)' }}></div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '18px', fontWeight: 700, color: theme.textPrimary }}>{topPerformer.hoursLogged}h</div>
                    <div style={{ fontSize: '11px', color: theme.textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Logged</div>
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.textSecondary }}>
                No data available.
              </div>
            )}
          </div>
        </motion.div>

        {/* Workload Distribution (Matching Horizontal Bar Chart list from image) */}
        <motion.div variants={itemVariants} style={{ gridColumn: 'span 4' }}>
          <div style={{ ...cardStyle, height: '320px', overflowY: 'auto' }}>
            <div style={{ fontSize: '14px', color: theme.textPrimary, fontWeight: 700, marginBottom: '4px' }}>
              Workload Capacity
            </div>
            <div style={{ fontSize: '12px', color: theme.textSecondary, marginBottom: '24px' }}>
              {selectedUserId === 'all' ? "Drilldown: User > Tasks" : 'Selected team member'}
            </div>
            
            {workload.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {workload.map(user => {
                  const capPct = Math.min((user.activeTasks / 10) * 100, 100);
                  
                  return (
                    <div key={user.user_id} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
                        <span style={{ fontWeight: 600, color: theme.textPrimary }}>{user.name}</span>
                        <span style={{ color: theme.textSecondary, fontSize: '11px' }}>{user.activeTasks} Tasks</span>
                      </div>
                      <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ flex: 1, height: '24px', background: 'var(--bg-hover)', borderRadius: '4px', overflow: 'hidden', position: 'relative' }}>
                          <motion.div 
                            initial={{ width: 0 }}
                            animate={{ width: `${capPct}%` }}
                            transition={{ duration: 1, delay: 0.2, type: 'spring' }}
                            style={{ 
                              height: '100%', 
                              background: 'var(--accent)', 
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-end',
                              paddingRight: '8px',
                              color: '#fff',
                              fontSize: '11px',
                              fontWeight: 600
                            }} 
                          >
                            {capPct >= 15 ? `${user.activeTasks}T` : ''}
                          </motion.div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.textSecondary }}>
                No workload data.
              </div>
            )}
          </div>
        </motion.div>

        {/* Bottom Row: Breakdown cards */}

        {/* Task Status Breakdown */}
        <motion.div variants={itemVariants} style={{ gridColumn: selectedUserId === 'all' ? 'span 4' : 'span 6' }}>
          <div style={{ ...cardStyle, height: '280px' }}>
            <div style={{ fontSize: '14px', color: theme.textPrimary, fontWeight: 700, marginBottom: '4px' }}>
              Task Status Breakdown
            </div>
            <div style={{ fontSize: '12px', color: theme.textSecondary, marginBottom: '20px' }}>
              {selectedUserId === 'all' ? 'Live across all tasks' : "This person's tasks"}
            </div>
            <StatBarList
              items={taskStatusData}
              emptyLabel="No tasks yet."
              textPrimary={theme.textPrimary}
              textSecondary={theme.textSecondary}
              trackBg="var(--bg-hover)"
            />
          </div>
        </motion.div>

        {/* Task Priority Breakdown */}
        <motion.div variants={itemVariants} style={{ gridColumn: selectedUserId === 'all' ? 'span 4' : 'span 6' }}>
          <div style={{ ...cardStyle, height: '280px' }}>
            <div style={{ fontSize: '14px', color: theme.textPrimary, fontWeight: 700, marginBottom: '4px' }}>
              Task Priority Breakdown
            </div>
            <div style={{ fontSize: '12px', color: theme.textSecondary, marginBottom: '20px' }}>
              {selectedUserId === 'all' ? 'Live across all tasks' : "This person's tasks"}
            </div>
            <StatBarList
              items={taskPriorityData}
              emptyLabel="No tasks yet."
              textPrimary={theme.textPrimary}
              textSecondary={theme.textSecondary}
              trackBg="var(--bg-hover)"
            />
          </div>
        </motion.div>

        {/* Project Status Breakdown */}
        {selectedUserId === 'all' && (
          <motion.div variants={itemVariants} style={{ gridColumn: 'span 4' }}>
          <div style={{ ...cardStyle, height: '280px' }}>
            <div style={{ fontSize: '14px', color: theme.textPrimary, fontWeight: 700, marginBottom: '4px' }}>
              Project Status Breakdown
            </div>
            <div style={{ fontSize: '12px', color: theme.textSecondary, marginBottom: '20px' }}>
              Live across all projects
            </div>
            <StatBarList
              items={projectStatusData}
              emptyLabel="No projects yet."
              textPrimary={theme.textPrimary}
              textSecondary={theme.textSecondary}
              trackBg="var(--bg-hover)"
            />
          </div>
        </motion.div>
        )}

        {/* Deals Pipeline */}
        {selectedUserId === 'all' && (
        <motion.div variants={itemVariants} style={{ gridColumn: 'span 6' }}>
          <div style={{ ...cardStyle, height: '300px' }}>
            <div style={{ fontSize: '14px', color: theme.textPrimary, fontWeight: 700, marginBottom: '4px' }}>
              Deals Pipeline
            </div>
            <div style={{ fontSize: '12px', color: theme.textSecondary, marginBottom: '20px' }}>
              Live by stage, with pipeline value
            </div>
            {dealsPipeline.reduce((s, d) => s + d.count, 0) > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', overflowY: 'auto' }}>
                {dealsPipeline.filter(d => d.count > 0).map(d => (
                  <div key={d.label} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: '90px', fontSize: '13px', fontWeight: 600, color: theme.textPrimary }}>{d.label}</div>
                    <div style={{ flex: 1, height: '22px', background: 'var(--bg-hover)', borderRadius: '4px', overflow: 'hidden' }}>
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${Math.min((d.count / Math.max(...dealsPipeline.map(x => x.count), 1)) * 100, 100)}%` }}
                        transition={{ duration: 0.8, type: 'spring' }}
                        style={{ height: '100%', background: d.color, borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: '8px', color: '#fff', fontSize: '11px', fontWeight: 600 }}
                      >
                        {d.count}
                      </motion.div>
                    </div>
                    <div style={{ width: '90px', textAlign: 'right', fontSize: '12px', color: theme.textSecondary }}>
                      ${Number(d.value).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.textSecondary }}>
                No deals yet.
              </div>
            )}
          </div>
        </motion.div>
        )}

        {/* Recent Activity */}
        <motion.div variants={itemVariants} style={{ gridColumn: selectedUserId === 'all' ? 'span 6' : 'span 12' }}>
          <div style={{ ...cardStyle, height: '300px', overflowY: 'auto' }}>
            <div style={{ fontSize: '14px', color: theme.textPrimary, fontWeight: 700, marginBottom: '4px' }}>
              Recent Activity
            </div>
            <div style={{ fontSize: '12px', color: theme.textSecondary, marginBottom: '20px' }}>
              Live from the activity log
            </div>
            {recentActivities.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {recentActivities.map((act) => {
                  const Icon = ACTIVITY_ICONS[(act.type || '').toLowerCase()] || FileText;
                  return (
                    <div key={act.activity_id || act.id} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                      <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'var(--bg-hover)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Icon size={15} color="var(--accent)" />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: theme.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {act.subject || act.type}
                        </div>
                        <div style={{ fontSize: '11px', color: theme.textSecondary }}>
                          {act.user?.name || 'Someone'}{act.client?.name ? ` · ${act.client.name}` : ''}
                        </div>
                      </div>
                      <div style={{ fontSize: '11px', color: theme.textSecondary, whiteSpace: 'nowrap' }}>
                        {timeAgo(act.date || act.created_at)}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.textSecondary }}>
                No recent activity.
              </div>
            )}
          </div>
        </motion.div>

      </motion.div>
    </div>
  );
}